import React, { useState, useEffect } from 'react';
import { TiptapEditor } from '../../components/TiptapEditor';
import {
    Box,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Switch,
    FormControlLabel,
    Typography,
    Tooltip,
    TablePagination,
    CircularProgress,
    InputAdornment,
    MenuItem,
    Select,
    FormControl,
    InputLabel
} from '@mui/material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
    Search as SearchIcon,
    CheckCircle as ActiveIcon,
    Cancel as InactiveIcon
} from '@mui/icons-material';
import { templateService, LessonPlanTemplate } from '../../services/templateService';
import { useSnackbar } from '../../hooks/useSnackbar';
import ConfirmDialog from '../../components/ConfirmDialog';
import { subjectService } from '../../services/subjectService';

export const LessonPlanTemplateManagement: React.FC = () => {
    const [templates, setTemplates] = useState<LessonPlanTemplate[]>([]);
    const [loading, setLoading] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState<LessonPlanTemplate | null>(null);
    const [formData, setFormData] = useState<Partial<LessonPlanTemplate>>({
        title: '',
        description: '',
        content: '',
        subject_id: '',
        is_active: true
    });
    const [subjects, setSubjects] = useState<any[]>([]);

    const { showSuccess, showError } = useSnackbar();

    const [confirmDialog, setConfirmDialog] = useState({
        open: false,
        title: '',
        content: '',
        onConfirm: () => { }
    });

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalRows, setTotalRows] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        fetchSubjects();
    }, []);

    const fetchSubjects = async () => {
        try {
            const res = await subjectService.getSubjects();
            setSubjects(res.subjects || []);
        } catch (error) {
            console.error(error);
        }
    };

    const fetchTemplates = async () => {
        try {
            setLoading(true);
            const res = await templateService.getAll({
                page: page + 1,
                limit: rowsPerPage,
                search: searchQuery
            });
            if (res && res.data) {
                setTemplates(res.data);
                setTotalRows(res.total || 0);
            } else {
                setTemplates([]);
                setTotalRows(0);
            }
        } catch (error) {
            console.error(error);
            showError('Không thể tải danh sách mẫu giáo án');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchTemplates();
        }, 500);
        return () => clearTimeout(timer);
    }, [page, rowsPerPage, searchQuery]);

    const handleOpen = (template?: LessonPlanTemplate) => {
        if (template) {
            setEditingTemplate(template);
            setFormData({
                title: template.title,
                description: template.description,
                content: template.content,
                subject_id: template.subject_id || '',
                is_active: template.is_active
            });
        } else {
            setEditingTemplate(null);
            setFormData({
                title: '',
                description: '',
                content: '',
                subject_id: '',
                is_active: true
            });
        }
        setOpenModal(true);
    };

    const handleClose = () => {
        setOpenModal(false);
        setEditingTemplate(null);
    };

    const handleSubmit = async () => {
        try {
            if (editingTemplate) {
                await templateService.update(editingTemplate.id, formData);
                showSuccess('Cập nhật thành công');
            } else {
                await templateService.create(formData);
                showSuccess('Thêm mới thành công');
            }
            handleClose();
            fetchTemplates();
        } catch (error) {
            showError('Có lỗi xảy ra');
        }
    };

    const handleDeleteClick = (id: string) => {
        setConfirmDialog({
            open: true,
            title: 'Xác nhận xóa',
            content: 'Bạn có chắc chắn muốn xóa mẫu giáo án này không?',
            onConfirm: () => handleDelete(id)
        });
    };

    const handleDelete = async (id: string) => {
        try {
            await templateService.delete(id);
            showSuccess('Đã xóa mẫu giáo án');
            fetchTemplates();
        } catch (error) {
            showError('Xóa thất bại');
        } finally {
            setConfirmDialog({ ...confirmDialog, open: false });
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">Quản lý Giáo án Mẫu (Templates)</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
                    Thêm mẫu mới
                </Button>
            </Box>

            <Box sx={{ mb: 2 }}>
                <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Tìm kiếm mẫu giáo án..."
                    value={searchQuery}
                    onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setPage(0);
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                    size="small"
                />
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell width="25%">Tên mẫu</TableCell>
                            <TableCell width="20%">Môn học</TableCell>
                            <TableCell width="35%">Mô tả</TableCell>
                            <TableCell width="10%">Trạng thái</TableCell>
                            <TableCell align="right" width="10%">Hành động</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                                    <CircularProgress size={20} />
                                </TableCell>
                            </TableRow>
                        ) : templates.map((template) => (
                            <TableRow key={template.id}>
                                <TableCell>{template.title}</TableCell>
                                <TableCell>{template.subject_name || 'N/A'}</TableCell>
                                <TableCell>{template.description}</TableCell>
                                <TableCell>
                                    {template.is_active ?
                                        <ActiveIcon color="success" fontSize="small" /> :
                                        <InactiveIcon color="disabled" fontSize="small" />
                                    }
                                </TableCell>
                                <TableCell align="right">
                                    <Tooltip title="Sửa">
                                        <IconButton size="small" onClick={() => handleOpen(template)}>
                                            <EditIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Xóa">
                                        <IconButton size="small" color="error" onClick={() => handleDeleteClick(template.id)}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                        {!loading && templates.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                                    <Typography color="text.secondary">Chưa có dữ liệu mẫu.</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    rowsPerPageOptions={[10, 25, 50]}
                    component="div"
                    count={totalRows}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                    labelRowsPerPage="Số dòng:"
                />
            </TableContainer>

            <Dialog open={openModal} onClose={handleClose} fullScreen>
                <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {editingTemplate ? 'Sửa mẫu giáo án' : 'Thêm mẫu mới'}
                    <Button onClick={handleClose} color="inherit">Đóng</Button>
                </DialogTitle>
                <DialogContent sx={{ p: 3 }}>
                    <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 1200, mx: 'auto' }}>
                        <TextField
                            label="Tên mẫu"
                            fullWidth
                            value={formData.title}
                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        />
                        <FormControl fullWidth>
                            <InputLabel>Môn học</InputLabel>
                            <Select
                                value={formData.subject_id}
                                label="Môn học"
                                onChange={(e) => setFormData({ ...formData, subject_id: e.target.value })}
                            >
                                <MenuItem value=""><em>-- Chung --</em></MenuItem>
                                {subjects.map((sub) => (
                                    <MenuItem key={sub.id} value={sub.id}>{sub.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            label="Mô tả"
                            fullWidth
                            multiline
                            rows={3}
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={!!formData.file_url}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            setFormData({ ...formData, file_url: 'http://', content: '' });
                                        } else {
                                            setFormData({ ...formData, file_url: '', content: '' });
                                        }
                                    }}
                                />
                            }
                            label="Dùng File đính kèm (Thay vì soạn thảo)"
                        />

                        {formData.file_url !== undefined && formData.file_url !== '' ? (
                            <TextField
                                label="Đường dẫn File (URL)"
                                fullWidth
                                value={formData.file_url}
                                onChange={(e) => setFormData({ ...formData, file_url: e.target.value })}
                                helperText="Nhập đường dẫn file mẫu (doc, xls, pdf...)"
                            />
                        ) : (
                            <Box>
                                <Typography variant="caption" color="textSecondary" gutterBottom>
                                    Nội dung mẫu (Soạn thảo):
                                </Typography>
                                <TiptapEditor
                                    content={formData.content || ''}
                                    onChange={(content) => {
                                        setFormData(prev => ({ ...prev, content: content }));
                                    }}
                                />
                            </Box>
                        )}

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.is_active}
                                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                />
                            }
                            label="Kích hoạt"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Hủy</Button>
                    <Button variant="contained" onClick={handleSubmit} disabled={!formData.title}>
                        Lưu
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                open={confirmDialog.open}
                title={confirmDialog.title}
                content={confirmDialog.content}
                onConfirm={confirmDialog.onConfirm}
                onCancel={() => setConfirmDialog({ ...confirmDialog, open: false })}
                confirmColor="error"
                confirmText="Xóa"
            />
        </Box>
    );
};
